import { useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { EmptyState, PrimaryButton, StatusPill, type StatusLevel } from '@/lib';
import { useTenants, type Tenant } from './queries';

const STATUS_TONE: Record<string, StatusLevel> = {
  active: 'healthy',
  suspended: 'warn',
  churned: 'critical',
};

export function TenantsList() {
  const list = useTenants();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const rows = list.data ?? [];
    return rows.filter((t) => {
      if (statusFilter !== 'all' && t.status !== statusFilter) return false;
      if (query) {
        const q = query.toLowerCase();
        const hay = [t.display_name, t.contact_email, t.id].join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [list.data, statusFilter, query]);

  if (list.isError) {
    return (
      <EmptyState
        variant="error"
        title="Couldn't load tenants"
        body={String(list.error)}
        cta={{ label: 'Retry', onClick: () => list.refetch() }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-sp-4">
      <header className="flex items-baseline justify-between">
        <div>
          <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
            Radiant superadmin
          </p>
          <h1 className="mt-sp-1 text-fs-h1 font-medium text-gray-900">Tenants</h1>
        </div>
        <PrimaryButton
          label="+ New tenant"
          onClick={() => navigate({ to: '/radiant/tenants/new' })}
        />
      </header>

      <div className="flex flex-wrap items-center gap-sp-3">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, email, id…"
          className="flex-1 rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1 text-fs-body text-gray-900"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Filter by status"
          className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1 text-fs-body text-gray-900"
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="churned">Churned</option>
        </select>
      </div>

      <div className="overflow-x-auto rounded-r-card border border-gray-300 bg-gray-50">
        <table className="min-w-full text-fs-meta">
          <thead>
            <tr>
              {['Display name', 'Contact', 'Plan', 'Status', 'Region', 'Created'].map(
                (h) => (
                  <th
                    key={h}
                    className="px-sp-3 py-sp-2 text-left font-medium uppercase tracking-[0.14em] text-gray-700"
                  >
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {filtered.map((t: Tenant) => (
              <tr key={t.id} className="border-t border-gray-300 hover:bg-gray-100">
                <td className="px-sp-3 py-sp-2 text-gray-900">
                  <Link
                    to="/radiant/tenants/$id"
                    params={{ id: t.id }}
                    className="font-medium text-info hover:underline"
                  >
                    {t.display_name}
                  </Link>
                </td>
                <td className="px-sp-3 py-sp-2 text-gray-900">{t.contact_email}</td>
                <td className="px-sp-3 py-sp-2 text-gray-900">{t.plan}</td>
                <td className="px-sp-3 py-sp-2">
                  <StatusPill level={STATUS_TONE[t.status] ?? 'info'} label={t.status} />
                </td>
                <td className="px-sp-3 py-sp-2 text-gray-900">{t.region || '—'}</td>
                <td className="px-sp-3 py-sp-2 font-mono text-gray-700">
                  {new Date(t.created_at).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && !list.isLoading ? (
          <p className="px-sp-4 py-sp-6 text-center text-fs-meta text-gray-700">
            {list.data?.length === 0
              ? 'No tenants yet — provision the first one.'
              : 'No tenants match the current filter.'}
          </p>
        ) : null}
      </div>
    </div>
  );
}
